import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { showToast } from "../utils/library";
import { SITENAMEALIAS, WEBSERVICE, isTestMode } from "../utils/init";
import { IpGeo, GetActiveCampaigns, GetWalletBalance } from "../utils/service";

const WS = WEBSERVICE;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getUrlParam(name) {
  const search =
    window.location.search ||
    (window.location.hash.includes("?")
      ? "?" + window.location.hash.split("?")[1]
      : "");
  return new URLSearchParams(search).get(name);
}

function isValidEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

function isOn(val) {
  return val === 1 || val === "1" || val === true || val === "true";
}

function parseCustomFields(raw) {
  if (!raw) return [];
  let arr = null;
  if (Array.isArray(raw)) {
    arr = raw;
  } else if (typeof raw === "string") {
    try {
      arr = JSON.parse(raw);
    } catch {
      return [];
    }
    if (!Array.isArray(arr)) return [];
  } else {
    return [];
  }
  if (!arr.length) return [];
  return arr.map((cf) => ({
    ...cf,
    label: cf.fieldLabel || cf.label || "",
    placeholder: cf.placeholderText || cf.placeholder || "",
    required: cf.required || false,
  }));
}

// ─── Currency Mapping ─────────────────────────────────────────────────────────
const CURRENCY_MAP = {
  USD: { symbol: "$", name: "US Dollar" },
  EUR: { symbol: "€", name: "Euro" },
  GBP: { symbol: "£", name: "British Pound" },
  JPY: { symbol: "¥", name: "Japanese Yen" },
  INR: { symbol: "₹", name: "Indian Rupee" },
  AUD: { symbol: "A$", name: "Australian Dollar" },
  CAD: { symbol: "C$", name: "Canadian Dollar" },
  CHF: { symbol: "CHF", name: "Swiss Franc" },
  SGD: { symbol: "S$", name: "Singapore Dollar" },
  HKD: { symbol: "HK$", name: "Hong Kong Dollar" },
  MYR: { symbol: "RM", name: "Malaysian Ringgit" },
  NZD: { symbol: "NZ$", name: "New Zealand Dollar" },
  BRL: { symbol: "R$", name: "Brazilian Real" },
  ZAR: { symbol: "R", name: "South African Rand" },
  BTC: { symbol: "₿", name: "Bitcoin" },
  ETH: { symbol: "Ξ", name: "Ethereum" },
  USDT: { symbol: "USDT", name: "Tether" },
  USDC: { symbol: "USDC", name: "USD Coin" },
  LTC: { symbol: "Ł", name: "Litecoin" },
  BCH: { symbol: "BCH", name: "Bitcoin Cash" },
  XRP: { symbol: "XRP", name: "Ripple" },
  DOGE: { symbol: "Ð", name: "Dogecoin" },
  HCX: { symbol: "HCX", name: "Hashcash" },
};

function getCurrencyDetails(code) {
  return CURRENCY_MAP[code] || { symbol: code, name: code };
}

function resolveCheckoutCurrency(
  detectedCode,
  availableCurrencies,
  defaultCurrency
) {
  // Find the currency in available currencies by code
  const found = availableCurrencies?.find((c) => c.currency === detectedCode);
  if (found) {
    const details = getCurrencyDetails(found.currency);
    return {
      code: found.currency,
      symbol: details.symbol,
      name: details.name,
      currencyId: found.currencyId,
    };
  }

  // If not found, use the default currency
  const defaultCurr =
    availableCurrencies?.find((c) => c.default) || availableCurrencies?.[0];
  if (defaultCurr) {
    const details = getCurrencyDetails(defaultCurr.currency);
    return {
      code: defaultCurr.currency,
      symbol: details.symbol,
      name: details.name,
      currencyId: defaultCurr.currencyId,
    };
  }

  // Fallback to USD
  return {
    code: "USD",
    symbol: "$",
    name: "US Dollar",
    currencyId: 1,
  };
}

// ─── Device ID ────────────────────────────────────────────────────────────────
function getDeviceId() {
  const KEY = `${SITENAMEALIAS}_device_id`;
  const stored = localStorage.getItem(KEY);
  if (stored) return stored;
  const signals = [
    navigator.platform || "",
    String(navigator.hardwareConcurrency || ""),
    String(window.screen.width),
    String(window.screen.height),
    String(window.screen.colorDepth),
    String(window.screen.pixelDepth || ""),
    String(new Date().getTimezoneOffset()),
    Intl.DateTimeFormat().resolvedOptions().timeZone || "",
  ];
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 280;
    canvas.height = 60;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#f0f0f0";
    ctx.fillRect(0, 0, 280, 60);
    ctx.textBaseline = "alphabetic";
    ctx.font = "bold 16px Arial";
    ctx.fillStyle = "#284ccf";
    ctx.fillText("BillBitcoins_fp_2024", 10, 28);
    ctx.font = "italic 13px Georgia";
    ctx.fillStyle = "rgba(102,204,0,0.85)";
    ctx.fillText("secure#checkout", 10, 48);
    ctx.beginPath();
    ctx.arc(250, 30, 18, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(40,76,207,0.4)";
    ctx.fill();
    signals.push(canvas.toDataURL());
  } catch (_) {}
  try {
    const gl =
      document.createElement("canvas").getContext("webgl") ||
      document.createElement("canvas").getContext("experimental-webgl");
    if (gl) {
      const ext = gl.getExtension("WEBGL_debug_renderer_info");
      if (ext) {
        signals.push(gl.getParameter(ext.UNMASKED_VENDOR_WEBGL) || "");
        signals.push(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) || "");
      }
    }
  } catch (_) {}
  const raw = signals.join("||");
  let h = 5381;
  for (let i = 0; i < raw.length; i++) {
    h = ((h << 5) + h) ^ raw.charCodeAt(i);
    h = h & h;
  }
  const id =
    "dev_" +
    (h >>> 0).toString(16).padStart(8, "0") +
    "_" +
    raw.length.toString(16);
  try {
    localStorage.setItem(KEY, id);
  } catch (_) {}
  return id;
}

// ─── Browser Session ID ───────────────────────────────────────────────────────
function getBrowserSessionId() {
  const KEY = `${SITENAMEALIAS}_browser_session_id`;
  let id = sessionStorage.getItem(KEY);
  if (!id) {
    id =
      "sess_" +
      Date.now().toString(36) +
      "_" +
      Math.random().toString(36).slice(2, 10);
    sessionStorage.setItem(KEY, id);
  }
  return id;
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function Checkout() {
  document.title = "Checkout";

  const routeParams = useParams() || {};
  const _raw = getUrlParam("data");
  const _decoded = (() => {
    try {
      return JSON.parse(atob(_raw || ""));
    } catch {
      return {};
    }
  })();
  const paymentId =
    decodeURIComponent(routeParams.paymentId || "") || _decoded.paymentId || "";
  const [merchantId, setMerchantId] = useState(_decoded.merchantId || "");
  const [brokerId, setBrokerId] = useState(
    _decoded.brokerId || _decoded.broker_id || ""
  );

  const [loading, setLoading] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [companyName, setCompanyName] = useState(
    localStorage.getItem(SITENAMEALIAS + "_companyName") || "PayBito"
  );
  const [exchangeName, setExchangeName] = useState(
    localStorage.getItem(SITENAMEALIAS + "_exchange") || "PayBito"
  );

  useEffect(() => {
    if (!brokerId) return;
    fetch(
      `https://accounts.paybito.com/api/home/getBrokerWiseExchangeInfo?brokerId=${encodeURIComponent(
        brokerId
      )}`
    )
      .then((r) => r.json())
      .then((res) => {
        const info = res?.value?.[0] || {};
        const name = info.exchange || "";
        if (name) {
          setExchangeName(name);
          localStorage.setItem(SITENAMEALIAS + "_exchange", name);
        }
      })
      .catch(() => {});
  }, [brokerId]);

  const [billingProfile, setBillingProfile] = useState(null);
  const [buyerProfile, setBuyerProfile] = useState(null);
  const [shippingProfile, setShippingProfile] = useState(null);
  const [products, setProducts] = useState([]);
  const [discounts, setDiscounts] = useState([]);

  const [subtotal, setSubtotal] = useState(0);
  const [feeLoading, setFeeLoading] = useState(false);
  const [feePercent, setFeePercent] = useState(0);
  const [feeAmount, setFeeAmount] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [feeApplied, setFeeApplied] = useState(false);

  // Discount states
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountSubtotal, setDiscountSubtotal] = useState(0);

  // ── Campaign / Reward states ──────────────────────────────────────────────
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  // ── Wallet balance states ─────────────────────────────────────────────────
  const [walletLedger, setWalletLedger] = useState(null); // array of { REWARD_TYPE, currentBalance, ... } or null
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletRedeemAmount, setWalletRedeemAmount] = useState(0);
  const [walletRedeemApplied, setWalletRedeemApplied] = useState(false);
  const [selectedWalletType, setSelectedWalletType] = useState(null); // 'cashback' | 'store_credit' | null

  // ── Trial period state ────────────────────────────────────────────────────
  // trialInfo: { trialDays, priceId, productName } — populated if any price has trialDays > 0
  const [trialInfo, setTrialInfo] = useState(null);
  const [useTrial, setUseTrial] = useState(true); // default checked

  const [detectedCurrency, setDetectedCurrency] = useState({
    code: "USD",
    symbol: "$",
    name: "US Dollar",
    currencyId: 1,
  });
  const [fxRate, setFxRate] = useState(1);
  const rawStoredEmail =
    typeof window !== "undefined" ? localStorage.getItem("email") : null;
  const isValidEmail = (v) =>
    typeof v === "string" &&
    v.trim() !== "" &&
    v.trim().toLowerCase() !== "null" &&
    v.trim().toLowerCase() !== "undefined" &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
  const storedEmail = isValidEmail(rawStoredEmail) ? rawStoredEmail.trim() : "";
  const [email, setEmail] = useState();
  const emailLocked = !!storedEmail;
  const [name, setName] = useState("");
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [stateProv, setStateProv] = useState("");
  const [country, setCountry] = useState("");
  const [zipcode, setZipcode] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [taxInfo, setTaxInfo] = useState("");
  const [refundAddress, setRefundAddress] = useState("");
  const [orderNotes, setOrderNotes] = useState("");
  const [customValues, setCustomValues] = useState({});
  const [errors, setErrors] = useState({});
  const profileFetchSeqRef = React.useRef(0);
  // Ref that always holds the latest detectedCurrency so async fetch callbacks
  // can read the current value without stale-closure issues and without needing
  // detectedCurrency in the effect dependency array (which caused recursive re-fetches).
  const detectedCurrencyRef = React.useRef(detectedCurrency);
  const walletFetchEmailRef = React.useRef("");
  const walletFetchCurrencyRef = React.useRef("");

  // ─── Fetch wallet balance by resolving customer identity from email ───────
  // ─── Fetch wallet balance by resolving customer identity from email ───────
  const fetchWalletBalanceForEmail = React.useCallback(
    (emailVal, currencyVal) => {
      if (
        !emailVal ||
        !merchantId ||
        (walletFetchEmailRef.current === emailVal &&
          walletFetchCurrencyRef.current === currencyVal)
      )
        return;
      walletFetchEmailRef.current = emailVal;
      walletFetchCurrencyRef.current = currencyVal;
      setWalletLoading(true);
      setWalletLedger(null);
      setWalletRedeemApplied(false);
      setWalletRedeemAmount(0);
      setSelectedWalletType(null);
      fetch(`${WS}/customeridentity/resolve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: emailVal,
          merchantId: parseInt(merchantId, 10),
        }),
      })
        .then((r) => r.json())
        .then((identityData) => {
          if (identityData.status && identityData.data?.customerId) {
            GetWalletBalance(
              merchantId,
              identityData.data.customerId,
              currencyVal
            )
              .then((res) => {
                if (res.data?.status) {
                  const walletData = res.data.data;
                  const ledgerList = walletData?.ledgerList;
                  if (
                    walletData?.returnId === "1" &&
                    Array.isArray(ledgerList) &&
                    ledgerList.length > 0
                  ) {
                    setWalletLedger(ledgerList);
                  } else {
                    setWalletLedger(null);
                  }
                } else {
                  setWalletLedger(null);
                }
              })
              .catch(() => {
                setWalletLedger(null);
              })
              .finally(() => setWalletLoading(false));
          } else {
            setWalletLoading(false);
          }
        })
        .catch(() => setWalletLoading(false));
    },
    [merchantId]
  );

  const isOneTimePayment = React.useMemo(() => {
    if (!products || products.length === 0) return true;

    // Check if ALL products have only one-time prices
    return products.every((product) =>
      (product.prices || []).every((price) => price.priceType === "one-time")
    );
  }, [products]);

  // ─── Discount Calculation ─────────────────────────────────────────────────
  const calculateDiscount = (subtotalValue, discountsList) => {
    if (!discountsList || discountsList.length === 0) {
      return {
        appliedDiscount: null,
        discountAmount: 0,
        discountedSubtotal: subtotalValue,
      };
    }
    let bestDiscount = null;
    let bestDiscountAmount = 0;
    for (const discount of discountsList) {
      const minValue = parseFloat(discount.minimumCartValue || 0);
      const discountPct = parseFloat(
        discount.discountPercentage || discount.discountPercent || 0
      );
      if (subtotalValue >= minValue && discountPct > 0) {
        const discountAmountValue = (subtotalValue * discountPct) / 100;
        if (!bestDiscount || discountAmountValue > bestDiscountAmount) {
          bestDiscount = discount;
          bestDiscountAmount = discountAmountValue;
        }
      }
    }
    if (bestDiscount) {
      return {
        appliedDiscount: bestDiscount,
        discountAmount: bestDiscountAmount,
        discountedSubtotal: subtotalValue - bestDiscountAmount,
      };
    }
    return {
      appliedDiscount: null,
      discountAmount: 0,
      discountedSubtotal: subtotalValue,
    };
  };

  useEffect(() => {
    if (storedEmail) {
      setEmail(storedEmail);
    }
  }, [storedEmail]);

  // Auto-fetch wallet balance whenever email becomes valid (pre-fill, auto-fill, or manual entry) or currency changes
  useEffect(() => {
    if (isValidEmail(email)) {
      console.log("Fetching wallet balance for email:", email, "currency:", detectedCurrency?.code);
      fetchWalletBalanceForEmail(email, detectedCurrency?.code || "USD");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, merchantId, detectedCurrency?.code]);

  // IP Geo
  useEffect(() => {
    document.title = "Checkout";
    IpGeo()
      .then((res) => {
        const cur = res.data?.currency;
        if (cur?.code)
          setDetectedCurrency({
            code: cur.code,
            symbol: cur.symbol || cur.code,
            name: cur.name || cur.code,
          });
      })
      .catch(() => {});
  }, []);

  // Keep detectedCurrencyRef in sync so profile fetch callbacks always read the latest value
  useEffect(() => {
    detectedCurrencyRef.current = detectedCurrency;
  }, [detectedCurrency]);

  // Load profile data
  useEffect(() => {
    if (!paymentId) {
      setShowErrorModal(true);
      setLoading(false);
      return;
    }

    const mySeq = ++profileFetchSeqRef.current;

    fetch(
      `${WS}/payment/profileDetailsById?id=${encodeURIComponent(paymentId)}`
    )
      .then((r) => r.json())
      .then((data) => {
        // Stale response — a newer detectedCurrency triggered a fresher fetch; discard this one
        if (mySeq !== profileFetchSeqRef.current) return;
        console.log("[Checkout] API response:", data);
        if (data.error !== 0 && data.error !== "0") {
          setShowErrorModal(true);
          setLoading(false);
          return;
        }

        const respMerchantId =
          data.merchantId != null ? String(data.merchantId) : merchantId;
        const respBrokerId =
          data.brokerId != null ? String(data.brokerId) : brokerId;
        if (respMerchantId !== merchantId) setMerchantId(respMerchantId);
        if (respBrokerId !== brokerId) setBrokerId(respBrokerId);

        const billing = data.billingProfiles?.[0] || null;
        const buyer = data.buyersProfiles?.[0] || null;
        const shipping = data.shippingProfiles?.[0] || null;
        const prods = data.products || [];
        const discountsList = data.discounts || [];

        setBillingProfile(billing);
        setBuyerProfile(buyer);
        setShippingProfile(shipping);
        setProducts(prods);
        setDiscounts(discountsList);

        // ── Fetch active campaigns ────────────────────────────────────
        const campaignSubtotal = parseFloat(
          prods
            .reduce((acc, p) => {
              const priceObj = p.prices?.[0];
              const ce =
                priceObj?.currencies?.find((c) => c.default) ||
                priceObj?.currencies?.[0];
              const qty = parseInt(p.itemQuantity ?? 1, 10);
              return (
                acc +
                parseFloat(
                  p.basePrice || p.price || p.amount || ce?.amount || 0
                ) *
                  qty
              );
            }, 0)
        );
        GetActiveCampaigns(respMerchantId, campaignSubtotal)
          .then((res) => {
            if (res.data?.status && Array.isArray(res.data?.data)) {
              setCampaigns(res.data.data);
            }
          })
          .catch(() => {});

        // ── Detect trial period from any price object ─────────────────
        let detectedTrial = null;
        let trialFlag = false;
        for (const prod of prods) {
          for (const price of prod.prices || []) {
            const td = parseInt(price.trialDays || price.trial_days || 0);
            if (td > 0) {
              detectedTrial = {
                trialDays: td,
                priceId: price.priceId || price.id || "",
                productName: prod.name || "this product",
              };
              trialFlag = true;
              break;
            }
          }
          if (detectedTrial) break;
        }
        setTrialInfo(detectedTrial);
        setUseTrial(trialFlag);

        const handlingPct = parseFloat(shipping?.handlingFeeValue || 0);
        const taxPct = parseFloat(shipping?.taxRate || 0);

        // Get available currencies from billing profile and product prices
        const profileCurrencies = billing?.currencies || [];
        const allPriceCurrencies = new Set(
          prods.flatMap((p) =>
            (p.prices || []).flatMap((pr) =>
              (pr.currencies || []).map((c) => c.currency)
            )
          )
        );

        // Resolve the effective currency based on:
        // 1. Check if detectedCurrency.code exists in product prices
        // 2. If not, use the currency with default: true
        // 3. If none have default, use the first available
        let effectiveCurrency;
        let needsFxConversion = false;
        let firstCode = null;

        // Try to find the detected currency in price currencies
        // Use ref to get the latest currency value (avoids stale closure and re-fetch loop)
        const latestCurrency = detectedCurrencyRef.current;
        const priceHasDetected = allPriceCurrencies.has(latestCurrency.code);

        if (priceHasDetected) {
          // Detected currency is available in prices - get its full details from prices
          const firstPrice = prods?.[0]?.prices?.[0];
          const detectedCurrencyObj = firstPrice?.currencies?.find(
            (c) => c.currency === latestCurrency.code
          );
          if (detectedCurrencyObj) {
            const details = getCurrencyDetails(latestCurrency.code);
            effectiveCurrency = {
              code: detectedCurrencyObj.currency,
              symbol: details.symbol,
              name: details.name,
              currencyId: detectedCurrencyObj.currencyId,
            };
          } else {
            effectiveCurrency = latestCurrency;
          }
        } else if (allPriceCurrencies.size > 0) {
          // Detected currency not in prices, find default or use first available
          const firstPrice = prods?.[0]?.prices?.[0];
          const defaultCurr = firstPrice?.currencies?.find((c) => c.default);
          const selectedCurr = defaultCurr || firstPrice?.currencies?.[0];

          if (selectedCurr) {
            effectiveCurrency = resolveCheckoutCurrency(
              selectedCurr.currency,
              [selectedCurr],
              selectedCurr
            );
            needsFxConversion = false;
            firstCode = selectedCurr.currency;
          } else {
            effectiveCurrency = {
              code: "USD",
              symbol: "$",
              name: "US Dollar",
            };
          }
        } else {
          // No price currencies found, fall back to USD
          effectiveCurrency = {
            code: "USD",
            symbol: "$",
            name: "US Dollar",
          };
        }

        // Update the detected currency state if the effective currency differs.
        // setDetectedCurrency here updates the display but no longer re-triggers
        // this effect (dependency changed to [paymentId] below).
        if (
          effectiveCurrency.code !== latestCurrency.code ||
          !latestCurrency.symbol ||
          !latestCurrency.name
        ) {
          setDetectedCurrency(effectiveCurrency);
        }

        // If FX conversion is needed, fetch the rate and calculate
        if (needsFxConversion && firstCode) {
          fetch(`${WS}/merchant/getMarketPrice?counter=${firstCode}`)
            .then((r) => r.json())
            .then((priceRes) => {
              if (
                (priceRes.error === "0" || priceRes.error === 0) &&
                parseFloat(priceRes.price) > 0
              ) {
                const rate = parseFloat(priceRes.price);
                setFxRate(rate);
                let usdSub = 0;
                prods.forEach((p) => {
                  const priceObj = p.prices?.[0];
                  const ce =
                    priceObj?.currencies?.find(
                      (c) => c.currency === firstCode
                    ) || priceObj?.currencies?.[0];
                  // Use product-level itemQuantity
                  const qty = parseInt(p.itemQuantity ?? 1, 10);
                  const base =
                    (parseFloat(
                      p.basePrice || p.price || p.amount || ce?.amount || 0
                    ) /
                      rate) *
                    qty;
                  usdSub += base;
                });
                usdSub = parseFloat(usdSub);
                setSubtotal(usdSub);
                const {
                  appliedDiscount: d,
                  discountAmount: da,
                  discountedSubtotal: ds,
                } = calculateDiscount(usdSub, discountsList);
                setAppliedDiscount(d);
                setDiscountAmount(da);
                setDiscountSubtotal(ds);
                const handlingAmtFx = parseFloat(
                  ((usdSub * handlingPct) / 100)
                );
                const taxAmtFx = parseFloat(
                  ((usdSub * taxPct) / 100)
                );
                const totalBeforeFeeFx = parseFloat(
                  (ds + handlingAmtFx + taxAmtFx)
                );
                setGrandTotal(totalBeforeFeeFx);

                // Process fee after totals are set
                const hasFee = isOn(data.isProcessingFeeApplied);
                if (hasFee && usdSub > 0) {
                  setFeeApplied(true);
                  setFeeLoading(true);
                  fetch(`${WS}/payment/calculateTransactionFee`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      merchantId: String(respMerchantId),
                      currencyId: "1",
                      amount: totalBeforeFeeFx,
                    }),
                  })
                    .then((r) => r.json())
                    .then((feeData) => {
                      if (feeData.error === "0" || feeData.error === 0) {
                        setFeePercent(parseFloat(feeData.txnCharge || 0));
                        setFeeAmount(parseFloat(feeData.transactionFee || 0));
                        setGrandTotal(
                          parseFloat(
                            feeData.totalAmount || totalBeforeFeeFx
                          )
                        );
                      } else {
                        showToast("error", feeData.error_msg || feeData.message || "Failed to calculate processing fee.");
                      }
                    })
                    .catch(() => {
                      showToast("error", "Failed to calculate processing fee.");
                    })
                    .finally(() => setFeeLoading(false));
                }
              }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
        } else {
          // No FX conversion needed - calculate directly
          let sub = 0;
          const cur = effectiveCurrency;
          prods.forEach((p) => {
            const priceObj = p.prices?.[0];
            const ce =
              priceObj?.currencies?.find((c) => c.currency === cur.code) ||
              priceObj?.currencies?.find((c) => c.default) ||
              priceObj?.currencies?.[0];
            // Use product-level itemQuantity
            const qty = parseInt(p.itemQuantity ?? 1, 10);
            const base =
              parseFloat(
                p.basePrice || p.price || p.amount || ce?.amount || 0
              ) * qty;
            sub += base;
          });
          sub = parseFloat(sub);
          setSubtotal(sub);

          const {
            appliedDiscount: discount,
            discountAmount: discAmount,
            discountedSubtotal: discSub,
          } = calculateDiscount(sub, discountsList);
          setAppliedDiscount(discount);
          setDiscountAmount(discAmount);
          setDiscountSubtotal(discSub);
          const handlingAmt = parseFloat(
            ((sub * handlingPct) / 100)
          );
          const taxAmt = parseFloat(((sub * taxPct) / 100));
          const totalBeforeFee = parseFloat(
            (discSub + handlingAmt + taxAmt)
          );
          setGrandTotal(totalBeforeFee);

          const hasFee = isOn(data.isProcessingFeeApplied);
          if (hasFee && sub > 0) {
            setFeeApplied(true);
            setFeeLoading(true);
            fetch(`${WS}/payment/calculateTransactionFee`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                merchantId: String(respMerchantId),
                currencyId: "1",
                amount: totalBeforeFee,
              }),
            })
              .then((r) => r.json())
              .then((feeData) => {
                if (feeData.error === "0" || feeData.error === 0) {
                  setFeePercent(parseFloat(feeData.txnCharge || 0));
                  setFeeAmount(parseFloat(feeData.transactionFee || 0));
                  setGrandTotal(
                    parseFloat(
                      feeData.totalAmount || totalBeforeFee
                    )
                  );
                } else {
                  showToast("error", feeData.error_msg || feeData.message || "Failed to calculate processing fee.");
                }
              })
              .catch(() => {
                showToast("error", "Failed to calculate processing fee.");
              })
              .finally(() => setFeeLoading(false));
          }
          setLoading(false);
        }
      })
      .catch((error) => {
        console.log("error", error);
        setShowErrorModal(true);
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentId]); // detectedCurrencyRef.current is used inside instead of detectedCurrency state

  // Form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    const cFields = parseCustomFields(buyerProfile?.customFields);
    const newErrors = {};

    if (!isValidEmail(email))
      newErrors.email = "Please enter a valid email address";
    if (isOn(buyerProfile?.collectFullName) && !name.trim())
      newErrors.name = "Please enter your full name";
    if (isOn(buyerProfile?.collectAddress)) {
      if (!address1.trim()) newErrors.address1 = "Please enter address line 1";
      if (!city.trim()) newErrors.city = "Please enter city";
      if (!stateProv.trim()) newErrors.state = "Please enter state/province";
      if (!zipcode.trim()) newErrors.zipcode = "Please enter zip/postal code";
      if (!country.trim()) newErrors.country = "Please enter country";
    }
    if (isOn(buyerProfile?.collectPhoneNumber) && !phone.trim())
      newErrors.phone = "Please enter phone number";
    if (isOn(buyerProfile?.collectCompanyName) && !company.trim())
      newErrors.company = "Please enter company name";
    if (isOn(buyerProfile?.collectTaxInfo) && !taxInfo.trim())
      newErrors.taxInfo = "Please enter tax information";
    cFields.forEach((cf, i) => {
      if (cf.required && !customValues[i]?.trim())
        newErrors[`custom_${i}`] = `Please enter ${cf.label}`;
    });

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setSubmitting(true);

    localStorage.setItem(SITENAMEALIAS + "_email", email);

    // ─── Step 1: Resolve customer identity ───────────────────────────────
    const identityPayload = {
      email,
      phone: phone || "",
      name: name || "",
      walletAddress: refundAddress || "",
      deviceId: getDeviceId(),
      sessionId: getBrowserSessionId(),
      PAYMENT_LINK: paymentId,
      shippingAddress: isOn(buyerProfile?.collectAddress)
        ? {
            street: address1 || "",
            city: city || "",
            state: stateProv || "",
            zip: zipcode || "",
            country: country || "",
          }
        : {},
      customFields: cFields.map((cf, i) => ({
        label: cf.label || "",
        value: customValues[i] || "",
        placeholder: cf.placeholder || "",
        helpText: cf.helpText || "",
      })),
      metadata: {
        orderNotes: orderNotes || "",
        source: "web_checkout",
        ...(company ? { company } : {}),
        ...(taxInfo ? { taxInfo } : {}),
      },
      merchantId: parseInt(merchantId, 10),
    };

    fetch(`${WS}/customeridentity/resolve`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(identityPayload),
    })
      .then((r) => r.json())
      .then((identityData) => {
        if (!identityData.status) {
          showToast(
            "error",
            identityData.message ||
              "Identity verification failed. Please try again."
          );
          setSubmitting(false);
          return;
        }
        console.log("Customer Identity:", JSON.stringify(identityData.data));
        const { identityId, customerId, isBrandWaletAbalebel } =
          identityData.data || {};
        const cur = detectedCurrency;

        // ─── Step 2: Create checkout session ─────────────────────────
        const lineItems = products.map((p) => {
          const priceObj = p.prices?.[0];
          const ce =
            priceObj?.currencies?.find((c) => c.currency === cur.code) ||
            priceObj?.currencies?.find((c) => c.default) ||
            priceObj?.currencies?.[0];
          return {
            productId: String(p.productId || p.id || ""),
            priceId: String(
              priceObj?.priceId || priceObj?.id || ce?.priceId || ""
            ),
            currency:
              ce?.currency || priceObj?.currencies?.[0]?.currency || "USD",
            // Use product-level itemQuantity
            quantity: String(p.itemQuantity ?? 1),
            isTrialAvailed: trialInfo && useTrial ? 1 : 0,
          };
        });

        const sessionPayload = {
          merchantId: String(merchantId),
          catalogueId: String(products[0]?.catalogId || ""),
          paymentLinkId: String(paymentId),
          customerIdentityId: String(identityId || ""),
          customerId: String(customerId || ""),
          billingProfileId: String(
            billingProfile?.billingProfileId || billingProfile?.id || ""
          ),
          shippingProfileId: String(
            shippingProfile?.shippingProfileId || shippingProfile?.id || ""
          ),
          discountProfileId: appliedDiscount
            ? String(appliedDiscount.id || "")
            : "",
          paymentMode: isOneTimePayment ? "one-time" : "recurring",
          campaignId: selectedCampaign
            ? String(selectedCampaign.campaignId || "")
            : "",
          redeemAmount: walletRedeemApplied ? walletRedeemAmount : 0,
          merchantCustomerLedgerId:
            walletRedeemApplied && selectedWalletType
              ? String(
                  walletLedger?.find(
                    (e) => e.REWARD_TYPE === selectedWalletType
                  )?.MERCHANTCUSTOMERLEDGERID || ""
                )
              : "",
          lineItems,
          // ── Trial period — included only when a trial exists ───────
          ...(trialInfo
            ? {
                trialPeriod: {
                  enabled: useTrial,
                  trialDays: useTrial ? trialInfo.trialDays : 0,
                  priceId: String(trialInfo.priceId || ""),
                },
              }
            : {}),
        };

        fetch(`${WS}/checkout/createCheckoutSession`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(sessionPayload),
        })
          .then((r) => r.json())
          .then((sessionData) => {
            if (!sessionData.status) {
              showToast(
                "error",
                sessionData.message ||
                  "Failed to create checkout session. Please try again."
              );
              setSubmitting(false);
              return;
            }

            const { sessionId } = sessionData.data || {};
            const redirectObj = {
              paymentId: String(paymentId),
              merchantId: String(merchantId),
              sessionId: String(sessionId),
              brokerId: String(brokerId),
              brandWalletAvaliabilityStatus: String(
                isBrandWaletAbalebel || "0"
              ),
              hasAvailedTrial: useTrial ? 1 : 0,
              redeemAmount: walletRedeemApplied ? walletRedeemAmount : 0,
              isZeroPayment:
                walletRedeemApplied &&
                Math.max(0, grandTotal - walletRedeemAmount) === 0
                  ? 1
                  : 0,
              campaignId: selectedCampaign
                ? String(selectedCampaign.campaignId || "")
                : "",
              // Include currency details for the next page
              currency: {
                code: detectedCurrency.code,
                symbol: detectedCurrency.symbol,
                name: detectedCurrency.name,
                currencyId: detectedCurrency.currencyId,
              },
            };
            window.location.href = `${
              window.location.origin
            }/merchant/checkout/payments/?data=${btoa(
              unescape(encodeURIComponent(JSON.stringify(redirectObj)))
            )}`;
          })
          .catch(() => {
            showToast(
              "error",
              "Network error creating checkout session. Please try again."
            );
            setSubmitting(false);
          });
      })
      .catch(() => {
        showToast(
          "error",
          "Network error verifying identity. Please try again."
        );
        setSubmitting(false);
      });
  };

  function clearErr(key) {
    setErrors((prev) => {
      const n = { ...prev };
      delete n[key];
      return n;
    });
  }

  if (loading) {
    return (
      <div
        style={{
          ...S.page,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <style>{GLOBAL_CSS}</style>
        <div style={{ textAlign: "center" }}>
          <div style={S.spinnerLg} />
          <p
            style={{
              marginTop: 12,
              fontSize: 14,
              color: "#6b7280",
              fontFamily: "'Roboto',sans-serif",
            }}
          >
            Please wait…
          </p>
        </div>
      </div>
    );
  }

  // Derived
  const bp = buyerProfile || {};
  const sp = shippingProfile || {};
  const hPct = parseFloat(sp.handlingFeeValue || 0);
  const tPct = parseFloat(sp.taxRate || 0);
  const cFields = parseCustomFields(bp.customFields);
  const cur = detectedCurrency;

  const showName = buyerProfile ? isOn(bp.collectFullName) : false;
  const showAddress = buyerProfile ? isOn(bp.collectAddress) : false;
  const showPhone = buyerProfile ? isOn(bp.collectPhoneNumber) : false;
  const showCompany = buyerProfile ? isOn(bp.collectCompanyName) : false;
  const showTax = buyerProfile ? isOn(bp.collectTaxInfo) : false;
  const showRefund = buyerProfile ? isOn(bp.collectCryptoRefundAddress) : false;
  const showNotes = buyerProfile ? isOn(bp.collectOrderNotes) : false;

  // Replace the current getBase function with this:
  const getBase = (p) => {
    const priceObj = p.prices?.[0];
    const ce =
      priceObj?.currencies?.find((c) => c.currency === cur.code) ||
      priceObj?.currencies?.find((c) => c.default) ||
      priceObj?.currencies?.[0];
    // Use product-level itemQuantity instead of inventory.quantity
    const qty = parseInt(p.itemQuantity || 1, 10);
    return (
      parseFloat(p.basePrice || p.price || p.amount || ce?.amount || 0) * qty
    );
  };

  const totalHandling = parseFloat(
    products.reduce((acc, p) => acc + (getBase(p) * hPct) / 100, 0)
  );
  const totalTax = parseFloat(
    products.reduce((acc, p) => acc + (getBase(p) * tPct) / 100, 0)
  );

  const getDiscountMessage = (discount) => {
    if (!discount) return null;
    const discountPct = discount.discountPercentage || discount.discountPercent;
    const minValue = discount.minimumCartValue;
    return `${discount.profileName} - ${discountPct}% off (Min. ${cur.symbol} ${minValue})`;
  };

  return (
    <div style={S.page}>
      <style>{GLOBAL_CSS}</style>

      {/* Test Mode Banner */}
      {isTestMode && (
        <div
          style={{
            background: "#fef3c7",
            borderBottom: "1px solid #fcd34d",
            padding: "8px 20px",
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            position: "sticky",
            top: 0,
            zIndex: 1000,
            boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#92400e"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <span
            style={{
              color: "#92400e",
              fontWeight: 800,
              fontSize: "13px",
              textTransform: "uppercase",
              letterSpacing: "1px",
            }}
          >
            Test Mode
          </span>
          {/* <span
            style={{
              color: "#b45309",
              fontSize: "12px",
              fontWeight: 500,
            }}
          >
            (No real payments will be processed)
          </span> */}
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div
          className="iv-modal-overlay"
          onClick={() => setShowErrorModal(false)}
        >
          <div className="iv-modal-box" onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "#fee2e2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ef4444"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h3
              style={{
                margin: "0 0 10px",
                fontSize: 20,
                fontWeight: 700,
                color: "#111827",
              }}
            >
              Payment Not Found
            </h3>
            <p
              style={{
                margin: "0 0 24px",
                fontSize: 14,
                color: "#6b7280",
                lineHeight: 1.6,
              }}
            >
              The payment details could not be found or the payment link is
              invalid. Please check the link and try again.
            </p>
            <button
              onClick={() => setShowErrorModal(false)}
              style={{
                padding: "12px 32px",
                background: "linear-gradient(135deg,#284ccf,#102ab3)",
                color: "#fff",
                WebkitTextFillColor: "#fff",
                border: "none",
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
                width: "100%",
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={S.header}>
        <div style={S.headerIcon}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#fff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="9" cy="21" r="1" />
            <circle cx="20" cy="21" r="1" />
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
          </svg>
        </div>
        <h2 style={S.headerTitle}>{companyName || "Secure Checkout"}</h2>
        <p style={S.headerSub}>Secure Checkout</p>
      </div>

      {/* Main Card */}
      <div className="iv-card" style={S.card}>
        <div style={S.cardAccent} />
        <div style={S.cardBody}>
          <div className="iv-layout" style={S.layout}>
            {/* ─── LEFT — Buyer Form ─── */}
            <div style={S.leftCol}>
              <div className="iv-section-label" style={S.sectionLabel}>
                Buyer Information
              </div>
              <p style={S.sectionSub}>
                Please fill in your details below to proceed with the payment.
              </p>

              <form id="checkout-form" onSubmit={handleSubmit} noValidate>
                {/* Email — always required */}
                <Field label="Email Address *" error={errors.email}>
                  <input
                    id="co-email"
                    type="email"
                    className="iv-input"
                    placeholder="Enter your email address"
                    value={email}
                    readOnly={emailLocked}
                    disabled={emailLocked}
                    onChange={(e) => {
                      if (emailLocked) return;
                      const value = e.target.value;
                      setEmail(value);
                      // Validate email format
                      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                        setErrors((prev) => ({
                          ...prev,
                          email: "Please enter a valid email address",
                        }));
                      } else {
                        clearErr("email");
                      }
                    }}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                        setErrors((prev) => ({
                          ...prev,
                          email: "Please enter a valid email address",
                        }));
                      }
                    }}
                    style={{
                      ...S.input,
                      borderColor: errors.email ? "#ef4444" : "#e2e6ef",
                      backgroundColor: emailLocked ? "#f3f4f6" : undefined,
                      cursor: emailLocked ? "not-allowed" : undefined,
                    }}
                  />
                </Field>

                {/* Name */}
                {showName && (
                  <Field
                    label={`Name${isOn(bp.collectFullName) ? " *" : ""}`}
                    error={errors.name}
                  >
                    <input
                      id="co-name"
                      type="text"
                      className="iv-input"
                      placeholder="Enter full name"
                      value={name}
                      onChange={(e) => {
                        // Allow letters, spaces, dots, hyphens, and apostrophes
                        let value = e.target.value;
                        value = value.replace(/[^a-zA-Z\s\.\-']/g, "");
                        setName(value);
                        if (isOn(bp.collectFullName) && !value.trim()) {
                          setErrors((prev) => ({
                            ...prev,
                            name: "Name is required",
                          }));
                        } else if (value.trim() && value.trim().length < 2) {
                          setErrors((prev) => ({
                            ...prev,
                            name: "Name must be at least 2 characters",
                          }));
                        } else {
                          clearErr("name");
                        }
                      }}
                      style={{
                        ...S.input,
                        borderColor: errors.name ? "#ef4444" : "#e2e6ef",
                      }}
                    />
                  </Field>
                )}

                {/* Address */}
                {showAddress && (
                  <>
                    <Field label="Shipping Address *" error={errors.address1}>
                      <input
                        id="co-addr1"
                        type="text"
                        className="iv-input"
                        placeholder="Shipping Address"
                        value={address1}
                        onChange={(e) => {
                          let value = e.target.value;
                          // Allow alphanumeric, spaces, commas, periods, hyphens, slashes
                          value = value.replace(/[^a-zA-Z0-9\s,\.\-\/#]/g, "");
                          setAddress1(value);
                          if (!value.trim()) {
                            setErrors((prev) => ({
                              ...prev,
                              address1: "Shipping address is required",
                            }));
                          } else {
                            clearErr("address1");
                          }
                        }}
                        style={{
                          ...S.input,
                          borderColor: errors.address1 ? "#ef4444" : "#e2e6ef",
                        }}
                      />
                    </Field>
                    <Field label="Address Line 2">
                      <input
                        id="co-addr2"
                        type="text"
                        className="iv-input"
                        placeholder="Shipping Address line 2"
                        value={address2}
                        onChange={(e) => {
                          let value = e.target.value;
                          value = value.replace(/[^a-zA-Z0-9\s,\.\-\/#]/g, "");
                          setAddress2(value);
                        }}
                        style={{ ...S.input, borderColor: "#e2e6ef" }}
                      />
                    </Field>
                    <div className="inlineGroup">
                      <Field label="City *" error={errors.city}>
                        <input
                          id="co-city"
                          type="text"
                          className="iv-input"
                          placeholder="City"
                          value={city}
                          onChange={(e) => {
                            let value = e.target.value;
                            value = value.replace(/[^a-zA-Z\s\.\-']/g, "");
                            setCity(value);
                            if (!value.trim()) {
                              setErrors((prev) => ({
                                ...prev,
                                city: "City is required",
                              }));
                            } else {
                              clearErr("city");
                            }
                          }}
                          style={{
                            ...S.input,
                            borderColor: errors.city ? "#ef4444" : "#e2e6ef",
                          }}
                        />
                      </Field>
                      <Field label="State/Province *" error={errors.state}>
                        <input
                          id="co-state"
                          type="text"
                          className="iv-input"
                          placeholder="State/Province"
                          value={stateProv}
                          onChange={(e) => {
                            let value = e.target.value;
                            value = value.replace(/[^a-zA-Z\s\.\-']/g, "");
                            setStateProv(value);
                            if (!value.trim()) {
                              setErrors((prev) => ({
                                ...prev,
                                state: "State/Province is required",
                              }));
                            } else {
                              clearErr("state");
                            }
                          }}
                          style={{
                            ...S.input,
                            borderColor: errors.state ? "#ef4444" : "#e2e6ef",
                          }}
                        />
                      </Field>
                    </div>
                    <div className="inlineGroup">
                      <Field label="Zip/Postal Code *" error={errors.zipcode}>
                        <input
                          id="co-zip"
                          type="text"
                          className="iv-input"
                          placeholder="Zip/Postal Code"
                          value={zipcode}
                          onChange={(e) => {
                            let value = e.target.value;
                            // Allow alphanumeric, spaces, hyphens (for postal codes like "12345-6789" or "SW1A 1AA")
                            value = value.replace(/[^a-zA-Z0-9\s\-]/g, "");
                            setZipcode(value);
                            if (!value.trim()) {
                              setErrors((prev) => ({
                                ...prev,
                                zipcode: "Zip/Postal code is required",
                              }));
                            } else {
                              clearErr("zipcode");
                            }
                          }}
                          style={{
                            ...S.input,
                            borderColor: errors.zipcode ? "#ef4444" : "#e2e6ef",
                          }}
                        />
                      </Field>
                      <Field label="Country *" error={errors.country}>
                        <input
                          id="co-country"
                          type="text"
                          className="iv-input"
                          placeholder="Country"
                          value={country}
                          onChange={(e) => {
                            let value = e.target.value;
                            value = value.replace(/[^a-zA-Z\s]/g, "");
                            setCountry(value);
                            if (!value.trim()) {
                              setErrors((prev) => ({
                                ...prev,
                                country: "Country is required",
                              }));
                            } else {
                              clearErr("country");
                            }
                          }}
                          style={{
                            ...S.input,
                            borderColor: errors.country ? "#ef4444" : "#e2e6ef",
                          }}
                        />
                      </Field>
                    </div>
                  </>
                )}

                {/* Phone */}
                {showPhone && (
                  <Field label="Phone Number *" error={errors.phone}>
                    <input
                      id="co-phone"
                      type="tel"
                      className="iv-input"
                      placeholder="Phone Number (e.g., +1234567890)"
                      value={phone}
                      onChange={(e) => {
                        let value = e.target.value;

                        // Only allow digits and + sign
                        let cleaned = value.replace(/[^\d+]/g, "");

                        // Ensure only one + sign at the beginning
                        const plusCount = (cleaned.match(/\+/g) || []).length;
                        if (plusCount > 1) {
                          cleaned = cleaned.replace(/\+/g, "");
                          cleaned = "+" + cleaned;
                        } else if (
                          plusCount === 1 &&
                          !cleaned.startsWith("+")
                        ) {
                          cleaned = cleaned.replace(/\+/g, "");
                          cleaned = "+" + cleaned;
                        }

                        // Remove any + that's not at the start
                        if (
                          cleaned.includes("+") &&
                          cleaned.indexOf("+") !== 0
                        ) {
                          cleaned = cleaned.replace(/\+/g, "");
                          if (value.startsWith("+")) cleaned = "+" + cleaned;
                        }

                        setPhone(cleaned);

                        // Validate phone number
                        if (!cleaned) {
                          setErrors((prev) => ({
                            ...prev,
                            phone: "Phone number is required",
                          }));
                        } else if (cleaned === "+") {
                          setErrors((prev) => ({
                            ...prev,
                            phone: "Please enter a valid phone number",
                          }));
                        } else {
                          const phoneRegex = /^\+?[0-9]{6,15}$/;
                          if (!phoneRegex.test(cleaned)) {
                            setErrors((prev) => ({
                              ...prev,
                              phone:
                                "Please enter a valid phone number (6-15 digits)",
                            }));
                          } else {
                            clearErr("phone");
                          }
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value && value !== "+") {
                          const phoneRegex = /^\+?[0-9]{6,15}$/;
                          if (!phoneRegex.test(value)) {
                            setErrors((prev) => ({
                              ...prev,
                              phone:
                                "Please enter a valid phone number (6-15 digits)",
                            }));
                          }
                        }
                      }}
                      style={{
                        ...S.input,
                        borderColor: errors.phone ? "#ef4444" : "#e2e6ef",
                      }}
                    />
                  </Field>
                )}

                {/* Company */}
                {showCompany && (
                  <Field label="Company Name *" error={errors.company}>
                    <input
                      id="co-company"
                      type="text"
                      className="iv-input"
                      placeholder="Company Name"
                      value={company}
                      onChange={(e) => {
                        let value = e.target.value;
                        // Allow alphanumeric, spaces, dots, hyphens, apostrophes, ampersand
                        value = value.replace(/[^a-zA-Z0-9\s\.\-'&]/g, "");
                        setCompany(value);
                        if (!value.trim()) {
                          setErrors((prev) => ({
                            ...prev,
                            company: "Company name is required",
                          }));
                        } else {
                          clearErr("company");
                        }
                      }}
                      style={{
                        ...S.input,
                        borderColor: errors.company ? "#ef4444" : "#e2e6ef",
                      }}
                    />
                  </Field>
                )}

                {/* Tax Info */}
                {showTax && (
                  <Field label="Tax Information *" error={errors.taxInfo}>
                    <input
                      id="co-tax"
                      type="text"
                      className="iv-input"
                      placeholder="Tax ID / VAT Number"
                      value={taxInfo}
                      onChange={(e) => {
                        let value = e.target.value;
                        // Allow alphanumeric, hyphens, spaces
                        value = value.replace(/[^a-zA-Z0-9\s\-]/g, "");
                        setTaxInfo(value);
                        if (!value.trim()) {
                          setErrors((prev) => ({
                            ...prev,
                            taxInfo: "Tax information is required",
                          }));
                        } else {
                          clearErr("taxInfo");
                        }
                      }}
                      style={{
                        ...S.input,
                        borderColor: errors.taxInfo ? "#ef4444" : "#e2e6ef",
                      }}
                    />
                  </Field>
                )}

                {/* Crypto Refund Address */}
                {showRefund && (
                  <Field label="Crypto Refund Address">
                    <input
                      id="co-refund"
                      type="text"
                      className="iv-input"
                      placeholder="Enter Crypto Refund Address (optional)"
                      value={refundAddress}
                      onChange={(e) => {
                        let value = e.target.value;
                        // Allow alphanumeric and common crypto address characters
                        value = value.replace(/[^a-zA-Z0-9]/g, "");
                        setRefundAddress(value);
                      }}
                      style={{ ...S.input, borderColor: "#e2e6ef" }}
                    />
                  </Field>
                )}

                {/* Order Notes */}
                {showNotes && (
                  <Field label="Order Notes">
                    <textarea
                      id="co-notes"
                      className="iv-input"
                      placeholder="Any special instructions or notes…"
                      value={orderNotes}
                      onChange={(e) => {
                        let value = e.target.value;
                        // Allow most characters but limit length
                        if (value.length <= 500) {
                          setOrderNotes(value);
                        }
                      }}
                      rows={3}
                      style={{
                        ...S.input,
                        height: "auto",
                        resize: "vertical",
                        paddingTop: 10,
                      }}
                    />
                  </Field>
                )}

                {/* Custom Fields */}
                {cFields.length > 0 &&
                  cFields.map((cf, i) => (
                    <Field
                      key={cf.customFieldId || i}
                      label={`${cf.label}${cf.required ? " *" : ""}`}
                      error={errors[`custom_${i}`]}
                    >
                      <input
                        type={
                          cf.fieldType === "email"
                            ? "email"
                            : cf.fieldType === "number"
                            ? "text"
                            : cf.fieldType || "text"
                        }
                        className="iv-input"
                        placeholder={cf.placeholder || cf.label}
                        value={customValues[i] || ""}
                        onChange={(e) => {
                          let value = e.target.value;

                          // Apply validation based on field type
                          if (cf.fieldType === "email") {
                            value = value.replace(/[^a-zA-Z0-9@._-]/g, "");
                            setCustomValues((prev) => ({
                              ...prev,
                              [i]: value,
                            }));
                            if (cf.required && !value) {
                              setErrors((prev) => ({
                                ...prev,
                                [`custom_${i}`]: `${cf.label} is required`,
                              }));
                            } else if (
                              value &&
                              !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
                            ) {
                              setErrors((prev) => ({
                                ...prev,
                                [`custom_${i}`]: `Please enter a valid email address`,
                              }));
                            } else {
                              clearErr(`custom_${i}`);
                            }
                          } else if (cf.fieldType === "number") {
                            // Only allow numbers and decimal point
                            value = value.replace(/[^\d.-]/g, "");
                            // Ensure only one decimal point
                            const decimalCount = (value.match(/\./g) || [])
                              .length;
                            if (decimalCount > 1) {
                              value = value.replace(/\./g, "");
                            }
                            // Ensure only one minus sign at start
                            if ((value.match(/-/g) || []).length > 1) {
                              value = value.replace(/-/g, "");
                            }
                            setCustomValues((prev) => ({
                              ...prev,
                              [i]: value,
                            }));
                            if (cf.required && !value) {
                              setErrors((prev) => ({
                                ...prev,
                                [`custom_${i}`]: `${cf.label} is required`,
                              }));
                            } else {
                              clearErr(`custom_${i}`);
                            }
                          } else if (cf.fieldType === "tel") {
                            // Phone number validation for custom fields
                            value = value.replace(/[^\d+]/g, "");
                            const plusCount = (value.match(/\+/g) || []).length;
                            if (plusCount > 1) {
                              value = value.replace(/\+/g, "");
                              value = "+" + value;
                            }
                            setCustomValues((prev) => ({
                              ...prev,
                              [i]: value,
                            }));
                            if (cf.required && !value) {
                              setErrors((prev) => ({
                                ...prev,
                                [`custom_${i}`]: `${cf.label} is required`,
                              }));
                            } else {
                              clearErr(`custom_${i}`);
                            }
                          } else {
                            // Default text validation
                            value = value.replace(/[<>]/g, ""); // Remove HTML tags
                            setCustomValues((prev) => ({
                              ...prev,
                              [i]: value,
                            }));
                            if (cf.required && !value.trim()) {
                              setErrors((prev) => ({
                                ...prev,
                                [`custom_${i}`]: `${cf.label} is required`,
                              }));
                            } else {
                              clearErr(`custom_${i}`);
                            }
                          }
                        }}
                        style={{
                          ...S.input,
                          borderColor: errors[`custom_${i}`]
                            ? "#ef4444"
                            : "#e2e6ef",
                        }}
                      />
                    </Field>
                  ))}

                {/* ── Trial Period Checkbox — shown only when trialDays > 0 ── */}
                {trialInfo && (
                  <div style={S.trialBox}>
                    <div style={S.trialHeader}>
                      {/* Gift/star icon */}
                      <div style={S.trialIconWrap}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#284ccf"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 12 20 22 4 22 4 12" />
                          <rect x="2" y="7" width="20" height="5" />
                          <line x1="12" y1="22" x2="12" y2="7" />
                          <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
                          <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
                        </svg>
                      </div>
                      <div>
                        <div style={S.trialTitle}>Free Trial Available</div>
                        <div style={S.trialSubtitle}>
                          Enjoy {trialInfo.trialDays} days free before your
                          subscription begins
                        </div>
                      </div>
                    </div>

                    <label style={S.trialCheckLabel}>
                      <div style={{ position: "relative", flexShrink: 0 }}>
                        <input
                          type="checkbox"
                          checked={useTrial}
                          onChange={(e) => setUseTrial(e.target.checked)}
                          style={{
                            position: "absolute",
                            opacity: 0,
                            width: 0,
                            height: 0,
                          }}
                        />
                        {/* Custom checkbox */}
                        <div
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: 6,
                            border: useTrial
                              ? "2px solid #284ccf"
                              : "2px solid #d1d5db",
                            background: useTrial ? "#284ccf" : "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            transition: "all .18s",
                            flexShrink: 0,
                          }}
                        >
                          {useTrial && (
                            <svg
                              width="11"
                              height="11"
                              viewBox="0 0 12 12"
                              fill="none"
                            >
                              <path
                                d="M2 6l3 3 5-5"
                                stroke="#fff"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: 13,
                          color: "#374151",
                          lineHeight: 1.5,
                          cursor: "pointer",
                        }}
                      >
                        Yes, I'd like to start with a{" "}
                        <strong style={{ color: "#284ccf" }}>
                          {trialInfo.trialDays}-day free trial
                        </strong>{" "}
                        — no charge until the trial period ends. I can cancel
                        anytime before it concludes.
                      </span>
                    </label>

                    {!useTrial && (
                      <div
                        style={{
                          marginTop: 10,
                          padding: "8px 12px",
                          background: "rgba(245,158,11,0.08)",
                          border: "1px solid rgba(245,158,11,0.25)",
                          borderRadius: 8,
                          fontSize: 12,
                          color: "#92400e",
                          WebkitTextFillColor: "#92400e",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 7,
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#f59e0b"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ flexShrink: 0, marginTop: 1 }}
                        >
                          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                          <line x1="12" y1="9" x2="12" y2="13" />
                          <line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                        <span
                          style={{
                            color: "#92400e",
                            WebkitTextFillColor: "#92400e",
                          }}
                        >
                          You've opted out of the free trial. Your subscription
                          billing will begin immediately upon checkout.
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </form>
            </div>

            {/* ─── RIGHT — Order Summary ─── */}
            <div style={S.rightCol}>
              <div className="iv-checkout-type" style={S.checkoutType}>
                {isOneTimePayment ? "One-time Payment" : "Recurring Payment"}
              </div>

              {/* Currency Display */}
              <div
                className="iv-currency-pill"
                style={{
                  marginBottom: 14,
                  padding: "8px 12px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  borderRadius: 20,
                  background: "rgba(40,76,207,0.08)",
                  border: "1px solid rgba(40,76,207,0.18)",
                  fontSize: 12,
                  color: "#111827",
                  WebkitTextFillColor: "#111827",
                  fontWeight: 600,
                }}
              >
                {/* <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v12M7 9h10a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2H7" />
                </svg> */}
                <span>
                  {cur.symbol} {cur.code} ({cur.name})
                </span>
              </div>

              <div style={S.divider} />

              {/* Products Table */}
              <div
                className="iv-tbl-wrap"
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  overflowX: "auto",
                  WebkitOverflowScrolling: "touch",
                  marginBottom: 16,
                  direction: "ltr",
                }}
              >
                <table className="iv-table" style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Item</th>
                      <th style={{ ...S.th, textAlign: "center" }}>Quantity</th>
                      <th style={{ ...S.th, textAlign: "right" }}></th>
                      <th style={{ ...S.th, textAlign: "right" }}>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p, i) => {
                      const base = getBase(p);
                      return (
                        <tr key={p.productId || p.id || i}>
                          <td style={S.td}>
                            <div style={{ fontWeight: 600, color: "#111827" }}>
                              {p.name}
                            </div>
                            {/* Show trial badge inline with product name if trial exists */}
                            {trialInfo && (
                              <div
                                style={{
                                  marginTop: 4,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 5,
                                  padding: "2px 8px",
                                  borderRadius: 20,
                                  background: "rgba(40,76,207,0.08)",
                                  border: "1px solid rgba(40,76,207,0.18)",
                                }}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="10"
                                  height="10"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="#284ccf"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <polyline points="20 12 20 22 4 22 4 12" />
                                  <rect x="2" y="7" width="20" height="5" />
                                </svg>
                                <span
                                  style={{
                                    fontSize: 11,
                                    color: "#284ccf",
                                    fontWeight: 600,
                                  }}
                                >
                                  {trialInfo.trialDays}-day free trial
                                </span>
                              </div>
                            )}
                          </td>
                          <td
                            style={{
                              ...S.td,
                              textAlign: "center",
                              color: "#374151",
                            }}
                          >
                            {p.itemQuantity ?? 1}
                          </td>
                          <td style={{ ...S.td, textAlign: "right" }}></td>
                          <td style={{ ...S.td, textAlign: "right" }}>
                            <span style={{ whiteSpace: "nowrap" }}>
                              {cur.symbol} {base} {cur.code}
                            </span>
                          </td>
                        </tr>
                      );
                    })}

                    {/* Handling Fee */}
                    {hPct > 0 && (
                      <tr>
                        <td
                          colSpan={2}
                          style={{ ...S.td, color: "#6b7280", fontSize: 12 }}
                        >
                          Handling Fee ({hPct}%)
                        </td>
                        <td
                          style={{
                            ...S.td,
                            textAlign: "right",
                            color: "#6b7280",
                            fontSize: 12,
                          }}
                        >
                          —
                        </td>
                        <td
                          style={{
                            ...S.td,
                            textAlign: "right",
                            fontWeight: 600,
                          }}
                        >
                          <span style={{ whiteSpace: "nowrap" }}>
                            {cur.symbol} {parseFloat(totalHandling).toFixed(2)} {cur.code}
                          </span>
                        </td>
                      </tr>
                    )}

                    {/* Tax */}
                    {tPct > 0 && (
                      <tr>
                        <td
                          colSpan={2}
                          style={{ ...S.td, color: "#6b7280", fontSize: 12 }}
                        >
                          Tax ({tPct}%)
                        </td>
                        <td
                          style={{
                            ...S.td,
                            textAlign: "right",
                            color: "#6b7280",
                            fontSize: 12,
                          }}
                        >
                          —
                        </td>
                        <td
                          style={{
                            ...S.td,
                            textAlign: "right",
                            fontWeight: 600,
                          }}
                        >
                          <span style={{ whiteSpace: "nowrap" }}>
                            {cur.symbol} {parseFloat(totalTax).toFixed(2)} {cur.code}
                          </span>
                        </td>
                      </tr>
                    )}

                    {/* Discount */}
                    {appliedDiscount && discountAmount > 0 && (
                      <tr>
                        <td
                          colSpan={2}
                          style={{ ...S.td, color: "#10b981", fontSize: 12 }}
                        >
                          Discount (
                          {appliedDiscount.discountPercentage ||
                            appliedDiscount.discountPercent}
                          %)
                          <span
                            style={{
                              fontSize: 10,
                              marginLeft: 6,
                              opacity: 0.8,
                            }}
                          >
                            <span style={{ whiteSpace: "nowrap" }}>
                              (Min. {cur.symbol}{" "}
                              {appliedDiscount.minimumCartValue})
                            </span>
                          </span>
                        </td>
                        <td
                          style={{
                            ...S.td,
                            textAlign: "right",
                            color: "#10b981",
                            fontSize: 12,
                          }}
                        >
                          —
                        </td>
                        <td
                          style={{
                            ...S.td,
                            textAlign: "right",
                            fontWeight: 600,
                            color: "#10b981",
                          }}
                        >
                          <span style={{ whiteSpace: "nowrap" }}>
                            -{cur.symbol} {parseFloat(discountAmount).toFixed(2)} {cur.code}
                          </span>
                        </td>
                      </tr>
                    )}

                    {/* Processing Fee */}
                    {feeApplied && (
                      <tr>
                        <td colSpan={2} style={S.td}>
                          {feeLoading ? (
                            <span
                              style={{ color: "#9ca3af", fontStyle: "italic" }}
                            >
                              Calculating fee…
                            </span>
                          ) : (
                            `Processing Fee (${feePercent}%)`
                          )}
                        </td>
                        <td style={{ ...S.td, textAlign: "right" }}>
                          {feeLoading ? "—" : ""}
                        </td>
                        <td style={{ ...S.td, textAlign: "right" }}>
                          {feeLoading ? (
                            "—"
                          ) : (
                            <span style={{ whiteSpace: "nowrap" }}>
                              {cur.symbol} {parseFloat(feeAmount).toFixed(2)} {cur.code}
                            </span>
                          )}
                        </td>
                      </tr>
                    )}
                    {/* Wallet Redeem */}
                    {walletRedeemApplied && walletRedeemAmount > 0 && (
                      <tr>
                        <td
                          colSpan={2}
                          style={{ ...S.td, color: "#0369a1", fontSize: 12 }}
                        >
                          Wallet Balance Applied
                        </td>
                        <td
                          style={{
                            ...S.td,
                            textAlign: "right",
                            color: "#0369a1",
                            fontSize: 12,
                          }}
                        >
                          —
                        </td>
                        <td
                          style={{
                            ...S.td,
                            textAlign: "right",
                            fontWeight: 600,
                            color: "#0369a1",
                          }}
                        >
                          <span style={{ whiteSpace: "nowrap" }}>
                            -{cur.symbol} {parseFloat(walletRedeemAmount).toFixed(2)}{" "}
                            {cur.code}
                          </span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th
                        colSpan={3}
                        style={{ ...S.tfootCell, textAlign: "left" }}
                      >
                        Total
                      </th>
                      <th style={{ ...S.tfootCell, textAlign: "right" }}>
                        {feeLoading ? (
                          "…"
                        ) : (
                          <span style={{ whiteSpace: "nowrap" }}>
                            {cur.symbol}{" "}
                            {Math.max(
                              0,
                              grandTotal - walletRedeemAmount
                            ).toFixed(2)}{" "}
                            {cur.code}
                          </span>
                        )}
                      </th>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Trial summary in order panel — shown when trial opted in */}
              {trialInfo && useTrial && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 12px",
                    background: "rgba(40,76,207,0.06)",
                    border: "1px solid rgba(40,76,207,0.15)",
                    borderRadius: 8,
                    marginBottom: 12,
                    fontSize: 12,
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#284ccf"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ flexShrink: 0 }}
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  <span className="iv-trial-free-text" style={{ color: "#1e3a8a" }}>
                    <strong>Free for {trialInfo.trialDays} days</strong> —
                    billing starts after your trial ends.
                  </span>
                </div>
              )}

              {/* Discount Banner */}
              {appliedDiscount && discountAmount > 0 && (
                <div
                  style={{
                    background: "#f0fdf4",
                    border: "1px solid #86efac",
                    borderRadius: 8,
                    padding: "8px 12px",
                    marginBottom: 12,
                    fontSize: 12,
                    color: "#166534",
                  }}
                >
                  🎉Discount Applied! You saved <br />
                  {cur.symbol} {discountAmount} {cur.code}
                  {getDiscountMessage(appliedDiscount)}
                  {/* <span style={{ whiteSpace: "nowrap" }}>
                    {cur.symbol} {discountAmount.toFixed(2)} {cur.code}
                  </span> */}
                  <span
                    style={{
                      fontSize: 10,
                      display: "block",
                      marginTop: 2,
                      color: "#2e7d32",
                    }}
                  ></span>
                </div>
              )}

              {/* Available Rewards / Campaigns */}
              {campaigns.length > 0 && (
                <div className="iv-rewards-box">
                  <div className="iv-rewards-header">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#6d28d9"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 12 20 22 4 22 4 12" />
                      <rect x="2" y="7" width="20" height="5" />
                      <line x1="12" y1="22" x2="12" y2="7" />
                      <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
                      <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
                    </svg>
                    Available Rewards
                    <span className="iv-rewards-badge">
                      {campaigns.length} offer{campaigns.length > 1 ? "s" : ""}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {campaigns.map((c) => {
                      const isSelected =
                        selectedCampaign?.campaignId === c.campaignId;
                      return (
                        <label
                          key={c.campaignId}
                          className={`iv-reward-item${
                            isSelected ? " selected" : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name="reward-campaign"
                            checked={isSelected}
                            onChange={() =>
                              setSelectedCampaign(isSelected ? null : c)
                            }
                            onClick={() => {
                              if (isSelected) setSelectedCampaign(null);
                            }}
                            style={{
                              accentColor: "#7c3aed",
                              width: 14,
                              height: 14,
                              flexShrink: 0,
                            }}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="iv-reward-name">
                              {c.campaignName}
                            </div>
                            <div className="iv-reward-meta">
                              {c.rewardType === "cashback"
                                ? "Cashback"
                                : "Store Credit"}
                              {c.minPurchase > 0 && (
                                <span className="iv-reward-min">
                                  {" "}
                                  · Min. {cur.symbol}
                                  {c.minPurchase}
                                </span>
                              )}
                            </div>
                          </div>
                          {isSelected ? (
                            <span className="iv-reward-selected-tag">
                              Selected
                            </span>
                          ) : (
                            <span className="iv-reward-pct-badge">
                              {c.rewardRate}% back
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                  {selectedCampaign && (
                    <div className="iv-reward-confirm">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#6d28d9"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ flexShrink: 0 }}
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <strong>{selectedCampaign.rewardRate}% cashback</strong>
                      &nbsp;will be credited after payment.
                    </div>
                  )}
                </div>
              )}

              {/* Wallet Balance */}
              {!walletLoading &&
                walletLedger !== null &&
                walletLedger.length > 0 &&
                walletLedger.reduce(
                  (acc, entry) => acc + parseFloat(entry.currentBalance ?? 0),
                  0
                ) > 0 && (
                  <div className="iv-wallet-box">
                    <div
                      className="iv-wallet-header"
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#0369a1"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect x="2" y="5" width="20" height="14" rx="2" />
                          <line x1="2" y1="10" x2="22" y2="10" />
                        </svg>
                        Rewards Wallet Balance
                      </div>
                      <span style={{ fontWeight: 700, color: "#0369a1" }}>
                        {cur.symbol}
                        {walletLedger
                          .reduce(
                            (acc, entry) =>
                              acc + parseFloat(entry.currentBalance ?? 0),
                            0
                          )}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#6b7280",
                        marginTop: "-4px",
                        marginBottom: "12px",
                        lineHeight: "1.4",
                        fontWeight: 400,
                      }}
                    >
                      Rewards & store credits issued by merchants will appear
                      here.
                    </div>
                    {walletLedger.map((entry) => {
                      const isStoreCr = entry.REWARD_TYPE === "store_credit";
                      const label = isStoreCr
                        ? "Store Credit Wallet"
                        : "Cashback Wallet";
                      const balance = parseFloat(entry.currentBalance ?? 0);
                      const isActive =
                        selectedWalletType === entry.REWARD_TYPE &&
                        walletRedeemApplied;
                      return (
                        <div
                          key={entry.REWARD_TYPE}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                            marginBottom: 8,
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: "#7c3aed",
                                marginBottom: 2,
                              }}
                            >
                              {label}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "baseline",
                                gap: 0,
                              }}
                            >
                              <span className="iv-wallet-amount">
                                {cur.symbol}
                                {balance}
                              </span>
                              <span className="iv-wallet-avail">
                                &nbsp;{cur.code} available
                              </span>
                            </div>
                          </div>
                          {balance > 0 && (
                            <div
                              role="button"
                              tabIndex={0}
                              className={`iv-btn-redeem ${
                                isActive ? "active" : "inactive"
                              }`}
                              onClick={() => {
                                if (isActive) {
                                  setWalletRedeemApplied(false);
                                  setWalletRedeemAmount(0);
                                  setSelectedWalletType(null);
                                } else {
                                  // Debug: Log current values
                                  console.log("Wallet Redemption Debug:", {
                                    balance,
                                    grandTotal,
                                    currentGrandTotal: grandTotal,
                                    walletRedeemAmount: walletRedeemAmount,
                                  });

                                  const toRedeem = Math.min(
                                    balance,
                                    grandTotal
                                  );
                                  const redeemValue = toRedeem;

                                  console.log("Redeeming amount:", redeemValue);
                                  console.log(
                                    "New total would be:",
                                    grandTotal - redeemValue
                                  );

                                  setWalletRedeemAmount(redeemValue);
                                  setWalletRedeemApplied(true);
                                  setSelectedWalletType(entry.REWARD_TYPE);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ")
                                  e.currentTarget.click();
                              }}
                            >
                              {isActive ? "Remove" : "Redeem"}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {walletRedeemApplied && walletRedeemAmount > 0 && (
                      <div className="iv-wallet-notice">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#0369a1"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ flexShrink: 0 }}
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        <strong>
                          {cur.symbol}
                          {walletRedeemAmount} {cur.code}
                        </strong>
                        &nbsp;will be deducted from your{" "}
                        {selectedWalletType === "store_credit"
                          ? "Store Credit"
                          : "Cashback"}{" "}
                        wallet.
                      </div>
                    )}
                  </div>
                )}

              {/* Order Total Card */}
              <div style={S.totalCard}>
                <div style={S.totalLabel}>
                  ORDER TOTAL <InfoIcon />
                </div>
                {appliedDiscount && discountAmount > 0 && (
                  <div
                    style={{
                      fontSize: 12,
                      color: "#6b7280",
                      textDecoration: "line-through",
                      marginBottom: 4,
                    }}
                  >
                    Original: {cur.symbol} {subtotal.toFixed(2)} {cur.code}
                  </div>
                )}
                {/* When trial is opted in, show $0.00 due today */}
                {trialInfo && useTrial ? (
                  <>
                    <div
                      className="iv-total-amount"
                      style={{ ...S.totalAmount, fontSize: "40px !important" }}
                    >
                      {cur.symbol} 0.00
                    </div>
                    <div
                      style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}
                    >
                      Due today (free trial)
                    </div>
                    <div
                      className="iv-trial-then-text"
                      style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}
                    >
                      Then{" "}
                      
                        {cur.symbol} {grandTotal.toFixed(2)} {cur.code}
                      
                      / {isOneTimePayment ? "one-time" : "recurring cycle"}{" "}
                      after {trialInfo.trialDays} days
                    </div>
                  </>
                ) : (
                  <>
                    {(() => {
                      // Calculate final amount - subtract wallet redemption only once
                      let displayAmount = grandTotal;
                      if (walletRedeemApplied && walletRedeemAmount > 0) {
                        displayAmount = Math.max(
                          0,
                          grandTotal - walletRedeemAmount
                        );
                      }
                      return (
                        <>
                          <div
                            className="iv-total-amount"
                            style={S.totalAmount}
                          >
                            {feeLoading
                              ? "…"
                              : `${cur.symbol} ${displayAmount.toFixed(2)}`}
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: "#6b7280",
                              marginTop: 4,
                            }}
                          >
                            {cur.code}
                          </div>
                          {walletRedeemApplied && walletRedeemAmount > 0 && (
                            <div
                              style={{
                                fontSize: 11,
                                color: "#0369a1",
                                marginTop: 8,
                              }}
                            >
                              Amount before wallet: {cur.symbol}{" "}
                              {grandTotal.toFixed(2)} {cur.code}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
              {/* Proceed to Payment */}
              <button
                type="submit"
                form="checkout-form"
                className="btn-iv"
                disabled={submitting || feeLoading}
                style={{
                  ...S.submitBtn,
                  marginTop: 16,
                  opacity: submitting || feeLoading ? 0.7 : 1,
                }}
              >
                {submitting ? (
                  <>
                    <span style={S.spinnerSm} /> Processing…
                  </>
                ) : (
                  <>
                    {trialInfo && useTrial
                      ? `Start Free Trial`
                      : `Select Payment Method`}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#fff"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <p
        style={{
          textAlign: "center",
          fontSize: 12,
          color: "#9ca3af",
          marginTop: 24,
          fontFamily: "'Roboto',sans-serif",
        }}
      >
        🔒 Payments secured by {exchangeName || "PayBito"}
      </p>
    </div>
  );
}

// ─── Field wrapper ────────────────────────────────────────────────────────────
function Field({ label, error, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <label
        style={{
          display: "block",
          fontSize: 11,
          fontWeight: 600,
          color: "#6b7280",
          textTransform: "uppercase",
          letterSpacing: ".5px",
          marginBottom: 6,
          fontFamily: "'Roboto',sans-serif",
        }}
      >
        {label}
      </label>
      {children}
      {error && (
        <span
          style={{
            display: "block",
            fontSize: 12,
            color: "#ef4444",
            marginTop: 4,
          }}
        >
          {error}
        </span>
      )}
    </div>
  );
}

// ─── Info icon ────────────────────────────────────────────────────────────────
function InfoIcon() {
  return (
    <span
      title="All amounts are in local currency."
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 15,
        height: 15,
        borderRadius: "50%",
        background: "#e2e6ef",
        color: "#6b7280",
        fontSize: 8,
        fontWeight: 800,
        marginLeft: 5,
        cursor: "default",
        verticalAlign: "middle",
      }}
    >
      ℹ
    </span>
  );
}

// ─── Global CSS ───────────────────────────────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700&display=swap');
  * { box-sizing:border-box; font-family:'Roboto',sans-serif!important; }
  body { margin:0; }
  @keyframes spin    { to { transform:rotate(360deg); } }
  @keyframes fadeUp  { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
  @keyframes scaleIn { from{transform:scaleX(0)} to{transform:scaleX(1)} }
  .iv-input:focus { border-color:#284ccf!important; box-shadow:0 0 0 3px rgba(40,76,207,.10)!important; outline:none; }
  .iv-input,
  #co-email,#co-name,#co-addr1,#co-addr2,#co-city,
  #co-state,#co-zip,#co-country,#co-phone,
  #co-company,#co-tax,#co-refund,#co-notes {
    background:#fff!important; background-color:#fff!important;
    color:#111827!important; -webkit-text-fill-color:#111827!important;
    border-color:#e2e6ef!important; box-shadow:none!important; filter:none!important;
  }
  .iv-input:-webkit-autofill,
  .iv-input:-webkit-autofill:hover,
  .iv-input:-webkit-autofill:focus {
    -webkit-box-shadow:0 0 0 1000px #fff inset!important;
    -webkit-text-fill-color:#111827!important;
  }
  .iv-input::placeholder { color:#b0b8c9!important; -webkit-text-fill-color:#b0b8c9!important; opacity:1!important; }
  .btn-iv:hover  { transform:translateY(-2px); box-shadow:0 10px 32px rgba(40,76,207,.40)!important; }
  .btn-iv:active { transform:translateY(0); }
  .iv-card,.iv-card * { color-scheme:light!important; }
  .iv-table { background:#fff!important; }
  .iv-table thead th { background:#f1f3f9!important; color:#374151!important; -webkit-text-fill-color:#374151!important; }
  .iv-table tbody tr { background:#fff!important; }
  .iv-table tbody tr td { background:#fff!important; color:#374151!important; -webkit-text-fill-color:#374151!important; border-color:#e2e6ef!important; }
  .iv-table tfoot tr th { background:#284ccf!important; color:#fff!important; -webkit-text-fill-color:#fff!important; }
  .iv-merchant-name  { font-size:18px!important; font-weight:800!important; color:#111827!important; }
  .iv-checkout-type  { font-size:13px!important; font-weight:700!important; color:#374151!important; }
  .iv-section-label  { font-size:18px!important; font-weight:800!important; color:#111827!important; }
  .iv-total-amount   { font-size:40px!important; font-weight:700!important; }
  tbody tr:hover td  { background:rgba(40,76,207,.025)!important; }
  .iv-modal-overlay  { position:fixed; inset:0; background:rgba(0,0,0,0.50); z-index:9999; display:flex; align-items:center; justify-content:center; animation:fadeUp .2s ease; }
  .iv-modal-box      { background:#fff!important; border-radius:16px; padding:40px 36px; max-width:420px; width:90%; text-align:center; box-shadow:0 24px 64px rgba(0,0,0,0.20); animation:fadeUp .25s ease; font-family:'Roboto',sans-serif!important; }
 @media(max-width:768px){ .iv-layout { flex-direction:column!important; } }
  @media(max-width:768px){
    .iv-tbl-wrap {
      width:100%!important;
      max-width:100%!important;
      overflow-x:auto!important;
      -webkit-overflow-scrolling:touch!important;
      border:1px solid #e2e6ef;
      border-radius:12px;
      direction:ltr!important;
    }
    .iv-table {
      border:none!important;
      border-radius:0!important;
      overflow:visible!important;
      width:auto!important;
      min-width:100%;
    }
    .iv-table thead th, .iv-table tbody td, .iv-table tfoot th { white-space:nowrap!important; }
  }
  @media(max-width:560px){
    .iv-card { padding:18px!important; }
    .iv-merchant-name { font-size:16px!important; }
    .iv-checkout-type { font-size:12px!important; }
    .iv-section-label { font-size:16px!important; }
    .iv-total-amount  { font-size:30px!important; }
    .iv-table { font-size:12px!important; }
    .iv-table thead th, .iv-table tbody td, .iv-table tfoot th { padding:10px 12px!important; }
    .btn-iv {
      padding:12px 18px!important;
      font-size:13px!important;
      gap:6px!important;
      line-height:1.25!important;
      white-space:normal!important;
      word-break:break-word!important;
      flex-wrap:wrap!important;
    }
    .btn-iv i, .btn-iv svg { flex-shrink:0; }
    .iv-modal-box { padding:28px 20px!important; }
  }
  .iv-currency-pill { color:#111827!important; -webkit-text-fill-color:#111827!important; background:rgba(40,76,207,0.08)!important; }
  .iv-currency-pill span { color:#111827!important; -webkit-text-fill-color:#111827!important; }
  .iv-trial-free-text { color:#1e3a8a!important; -webkit-text-fill-color:#1e3a8a!important; }
  .iv-trial-then-text { color:#374151!important; -webkit-text-fill-color:#374151!important; font-size:12px!important; }

  /* ── Rewards section ────────────────────────────────────────────── */
  .iv-rewards-box { background:linear-gradient(135deg,#faf5ff 0%,#f5f0ff 100%); border:1.5px solid #c4b5fd; border-radius:12px; padding:14px 14px 10px; margin-bottom:12px; }
  .iv-rewards-header { font-size:12px; font-weight:700; color:#6d28d9!important; -webkit-text-fill-color:#6d28d9!important; margin-bottom:10px; display:flex; align-items:center; gap:6px; }
  .iv-rewards-badge { margin-left:auto; background:#7c3aed; color:#fff!important; -webkit-text-fill-color:#fff!important; border-radius:12px; font-size:10px; font-weight:700; padding:2px 8px; }
  .iv-reward-item { display:flex; align-items:center; gap:8px; cursor:pointer; padding:9px 11px; border-radius:8px; border:1.5px solid #e9d5ff; background:#fff; transition:all 0.15s; margin-bottom:5px; }
  .iv-reward-item.selected { border-color:#7c3aed; background:linear-gradient(135deg,#ede9fe,#f5f0ff); box-shadow:0 2px 8px rgba(124,58,237,0.12); }
  .iv-reward-name { font-size:12px; font-weight:700; color:#3b0764!important; -webkit-text-fill-color:#3b0764!important; }
  .iv-reward-meta { font-size:11px; color:#7c3aed!important; -webkit-text-fill-color:#7c3aed!important; margin-top:2px; }
  .iv-reward-min { color:#6b7280!important; -webkit-text-fill-color:#6b7280!important; margin-left:4px; }
  .iv-reward-pct-badge { background:linear-gradient(135deg,#7c3aed,#6d28d9); color:#fff!important; -webkit-text-fill-color:#fff!important; border-radius:20px; font-size:10px; font-weight:700; padding:2px 8px; white-space:nowrap; margin-left:auto; }
  .iv-reward-selected-tag { font-size:10px; font-weight:700; color:#7c3aed!important; -webkit-text-fill-color:#7c3aed!important; background:#ede9fe; border:1px solid #c4b5fd; border-radius:10px; padding:2px 8px; white-space:nowrap; }
  .iv-reward-confirm { margin-top:8px; font-size:11px; color:#6d28d9!important; -webkit-text-fill-color:#6d28d9!important; background:#ede9fe; border-radius:6px; padding:6px 10px; display:flex; align-items:center; gap:5px; }
  .iv-reward-confirm strong { color:#6d28d9!important; -webkit-text-fill-color:#6d28d9!important; font-weight:700; }

  /* ── Wallet section ─────────────────────────────────────────────── */
  .iv-wallet-box { background:linear-gradient(135deg,#f0f9ff 0%,#e8f4fd 100%); border:1.5px solid #7dd3fc; border-radius:12px; padding:14px 14px 10px; margin-bottom:12px; }
  .iv-wallet-header { font-size:12px; font-weight:700; color:#0369a1!important; -webkit-text-fill-color:#0369a1!important; margin-bottom:10px; display:flex; align-items:center; gap:6px; }
  .iv-wallet-amount { font-size:22px; font-weight:800; color:#0c4a6e!important; -webkit-text-fill-color:#0c4a6e!important; line-height:1; }
  .iv-wallet-avail { font-size:11px; color:#64748b!important; -webkit-text-fill-color:#64748b!important; margin-left:5px; }
  .iv-wallet-nil { font-size:12px; color:#94a3b8!important; -webkit-text-fill-color:#94a3b8!important; }
  .iv-wallet-notice { margin-top:8px; font-size:11px; color:#0369a1!important; -webkit-text-fill-color:#0369a1!important; background:#e0f2fe; border-radius:6px; padding:6px 10px; display:flex; align-items:center; gap:5px; }
  .iv-wallet-notice strong { color:#0369a1!important; -webkit-text-fill-color:#0369a1!important; font-weight:700; }
  .iv-btn-redeem { font-size:11px; font-weight:700; padding:5px 14px; border-radius:7px; border:1.5px solid #0369a1; cursor:pointer; white-space:nowrap; transition:all 0.15s; }
  .iv-btn-redeem.active { background:#0369a1; color:#fff!important; -webkit-text-fill-color:#fff!important; }
  .iv-btn-redeem.inactive { background:#fff; color:#0369a1!important; -webkit-text-fill-color:#0369a1!important; }
`;

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  page: {
    background: "linear-gradient(135deg,#e8ecf8 0%,#f0f2fa 50%,#eef0f7 100%)",
    minHeight: "100vh",
    fontFamily: "'Roboto',sans-serif",
    padding: "40px 20px 60px",
  },
  header: {
    textAlign: "center",
    marginBottom: 32,
    animation: "fadeUp .5s ease",
  },
  headerIcon: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    background: isTestMode
      ? "linear-gradient(135deg,#f59e0b,#d97706)"
      : "linear-gradient(135deg,#284ccf,#102ab3)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
    boxShadow: isTestMode
      ? "0 8px 24px rgba(245,158,11,.30)"
      : "0 8px 24px rgba(40,76,207,.30)",
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: isTestMode ? "#d97706" : "#284ccf",
    margin: "0 0 4px",
  },
  headerSub: { fontSize: 14, color: "#6b7280", margin: 0 },
  card: {
    maxWidth: 1040,
    margin: "0 auto",
    background: "#fff",
    backgroundColor: "#fff",
    borderRadius: 16,
    boxShadow: "0 8px 30px rgba(0,0,0,.08)",
    border: isTestMode
      ? "1px solid rgba(245,158,11,.12)"
      : "1px solid rgba(40,76,207,.08)",
    overflow: "hidden",
    animation: "fadeUp .5s ease .1s both",
  },
  cardAccent: {
    height: 4,
    background: isTestMode
      ? "linear-gradient(90deg,#f59e0b,#d97706,#10b981)"
      : "linear-gradient(90deg,#284ccf,#102ab3,#10b981)",
    transformOrigin: "left",
    animation: "scaleIn .8s ease .3s both",
  },
  cardBody: { padding: 32, background: "#fff", backgroundColor: "#fff" },
  layout: { display: "flex", gap: 40 },
  leftCol: { flex: 1, minWidth: 0 },
  rightCol: { flex: 1, minWidth: 0 },
  sectionLabel: {
    fontSize: 18,
    fontWeight: 700,
    color: "#111827",
    paddingBottom: 14,
    marginBottom: 10,
    borderBottom: "2px solid #e2e6ef",
    borderLeft: isTestMode ? "4px solid #d97706" : "4px solid #284ccf",
    paddingLeft: 12,
  },
  sectionSub: {
    fontSize: 13,
    color: "#6b7280",
    margin: "0 0 22px",
    lineHeight: 1.6,
  },
  inlineGroup: (isMobile) => ({
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
    gap: 16,
  }),
  input: {
    width: "100%",
    height: 46,
    padding: "10px 16px",
    border: "1.5px solid #e2e6ef",
    borderRadius: 10,
    fontSize: 14,
    color: "#111827",
    background: "#fff",
    WebkitTextFillColor: "#111827",
    transition: "all .2s",
    fontFamily: "'Roboto',sans-serif",
  },
  merchantName: {
    fontSize: 18,
    fontWeight: 700,
    color: "#111827",
    marginBottom: 4,
  },
  checkoutType: {
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 4,
  },
  divider: { height: 1, background: "#e2e6ef", margin: "16px 0" },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    border: "1px solid #e2e6ef",
    borderRadius: 12,
    fontSize: 13,
    marginBottom: 16,
    overflow: "hidden",
  },
  th: {
    background: "#f1f3f9",
    color: "#374151",
    WebkitTextFillColor: "#374151",
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".5px",
    padding: "12px 14px",
    border: "none",
    textAlign: "left",
  },
  td: {
    padding: "12px 14px",
    color: "#374151",
    background: "#fff",
    borderTop: "1px solid #e2e6ef",
    transition: "background .15s",
  },
  tfootCell: {
    padding: "13px 14px",
    textAlign: "left",
    fontSize: 14,
    fontWeight: 700,
    background: isTestMode ? "#d97706" : "#284ccf",
    color: "#fff",
    WebkitTextFillColor: "#fff",
  },
  totalCard: {
    background: isTestMode
      ? "linear-gradient(135deg,rgba(245,158,11,0.08),rgba(217,119,6,0.04))"
      : "linear-gradient(135deg,rgba(40,76,207,0.08),rgba(16,42,179,0.04))",
    border: isTestMode
      ? "1px solid rgba(245,158,11,0.15)"
      : "1px solid rgba(40,76,207,0.15)",
    borderRadius: 12,
    padding: "20px 24px",
    marginTop: 16,
  },
  totalLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: ".8px",
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
  },
  totalAmount: {
    background: isTestMode
      ? "linear-gradient(135deg,#f59e0b,#d97706)"
      : "linear-gradient(135deg,#284ccf,#102ab3)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    whiteSpace: "nowrap",
  },
  submitBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    width: "100%",
    padding: "14px 40px",
    background: isTestMode
      ? "linear-gradient(135deg,#f59e0b,#d97706)"
      : "linear-gradient(135deg,#284ccf,#102ab3)",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: isTestMode
      ? "0 6px 20px rgba(245,158,11,.30)"
      : "0 6px 20px rgba(40,76,207,.30)",
    transition: "all .25s ease",
    fontFamily: "'Roboto',sans-serif",
  },
  spinnerSm: {
    display: "inline-block",
    width: 13,
    height: 13,
    border: "2px solid rgba(255,255,255,.3)",
    borderTop: "2px solid #fff",
    borderRadius: "50%",
    animation: "spin .8s linear infinite",
    flexShrink: 0,
  },
  spinnerLg: {
    display: "inline-block",
    width: 36,
    height: 36,
    border: "3px solid rgba(40,76,207,.2)",
    borderTop: "3px solid #284ccf",
    borderRadius: "50%",
    animation: "spin .8s linear infinite",
    margin: "0 auto",
  },

  // ── Trial period styles ───────────────────────────────────────────────────
  trialBox: {
    marginTop: 24,
    padding: "18px 20px",
    borderRadius: 14,
    background:
      "linear-gradient(135deg,rgba(40,76,207,0.05),rgba(40,76,207,0.02))",
    border: "1.5px solid rgba(40,76,207,0.20)",
  },
  trialHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    marginBottom: 14,
  },
  trialIconWrap: {
    width: 38,
    height: 38,
    borderRadius: 10,
    background: "rgba(40,76,207,0.10)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  trialTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "#111827",
    marginBottom: 2,
  },
  trialSubtitle: { fontSize: 12, color: "#6b7280", lineHeight: 1.5 },
  trialCheckLabel: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: "12px 14px",
    borderRadius: 10,
    background: "#fff",
    border: "1px solid rgba(40,76,207,0.12)",
    cursor: "default",
  },
};
